import { Component, HostListener, NgZone, Signal } from '@angular/core'
import { CalendarHeaderComponent } from '../calendar-header/calendar-header.component'
import { CalendarDaysComponent } from '../calendar-days/calendar-days.component'
import { CalendarViewService } from '@app/routes/dashboard/services/calendar-view.service'
import { UserService } from '@app/core/_services/user.service'
import { Subject, timer } from 'rxjs'
import {
    BookSessionPreferences,
    ConfirmedSessionsResponse,
    Container,
    User,
} from '@app/core/_models'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import {
    combineLatestWith,
    debounceTime,
    distinctUntilChanged,
    filter,
    finalize,
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators'
import { MS_IN_ONE_DAY, TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import { AvailableTimesService } from '@app/core/_services/available-times.service'
import {
    BookSessionInfo,
    BookSessionsService,
    ConfirmedSessionsService,
    FeatureCheckService,
    SelectedSession,
    SelectedSessionService,
    TWO_MINS_IN_MSECS,
    UtilsService,
} from '@app/core/_services'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import {
    UserRelationshipActionTaken,
    UserRelationshipService,
} from '@app/core/_services/user-relationship.service'
import { AnalyticsService, BookingSource } from '@app/core/_services/analytics.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { LoginSignupUtilService } from '@app/core/_services/login-signup-util.service'
import { WelcomeChecklistService } from '@app/core/_services/welcome-checklist.service'
import { environment } from '@env/environment'
import { FeatureBannerComponent } from '@app/shared/components/feature-banner/feature-banner.component'
import { CommonModule } from '@angular/common'
import { ButtonModule } from 'primeng/button'
import { MODAL_VERIFY_EMAIL_BOOKING } from '@app/shared/components/email-verify/email-verify.component'
import { ErrorHandlerService } from '@app/core/_services/error-handler.service'
import { RewardsReferralService } from '@app/core/_services/rewards-referral.service'

export interface DayData {
    startOfDayInMs?: number
    isToday?: boolean
}

// KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
const FEATURE_TWENTY_FOUR_HOUR_BANNER_EXPIRY = 1718841600000 // Thursday, June 20, 2024 12:00:00 AM
const FEATURE_TWENTY_FOUR_HOUR_BANNER_DISMISSED = 'twentyFourHourBannerDismissed'

@Component({
    selector: 'fm-calendar',
    imports: [
        FeatureBannerComponent,
        CalendarHeaderComponent,
        CalendarDaysComponent,
        CommonModule,
        ButtonModule,
    ],
    templateUrl: './calendar.component.html',
    styleUrl: './calendar.component.scss',
})
export class CalendarComponent {
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public currentUser: User
    private numberOfDaysToShow: number
    private calendarStartDayMidnight: number
    public sessionDuration: number
    public days: Array<DayData> = new Array<DayData>({})
    public initialized: boolean = false

    // Limit the amount of data we request to populate the calendar
    // based on what timeslots are currently visible to the user
    private endTimeToRetrieve: number = 0
    private pollTimerStarted: boolean = false
    private dateRange$ = new Subject<any>()

    // Prevent unmatched and session reuqests from going out when window is
    // blured TODO: Add full logic
    private timedOut: boolean = false
    private blurred: boolean = false

    // Query confirmed sessions if unmatched session is starting
    private unmatchedSessionStartingTimeout = null

    public isWelcomeChecklistActive: Signal<boolean> =
        this.welcomeChecklistService.isWelcomeChecklistActive

    // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
    showFeatureBanner: boolean = false

    public selectedSessions: SelectedSession[] = []
    public bookingInProgress: boolean = false

    // Temporary until we figure out ghosting issues with
    // PrimeNg scrollpanel
    public scrollbarWidth: number = 15

    constructor(
        private userService: UserService,
        private timeUtilitiesService: TimeUtilitiesService,
        private calendarViewService: CalendarViewService,
        private availableTimesService: AvailableTimesService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private sessionSettingsService: SessionSettingsService,
        private userRelationshipService: UserRelationshipService,
        private featureCheckService: FeatureCheckService,
        private analyticsService: AnalyticsService,
        private dialogService: DialogService,
        private lUtil: LoginSignupUtilService,
        private utilsService: UtilsService,
        private welcomeChecklistService: WelcomeChecklistService,
        private bookSessionsService: BookSessionsService,
        private errorHandlerService: ErrorHandlerService,
        private rewardsReferralService: RewardsReferralService,
        private ngZone: NgZone,
        public selectedSessionService: SelectedSessionService,
    ) {}

    @HostListener('window:focus', ['$event'])
    onFocus(event: any): void {
        this.blurred = false

        if (this.timedOut === true) {
            this.timedOut = false
            this.queryConfirmedSessions()
            this.queryUnmatchedSessions()
        }
    }

    @HostListener('window:blur', ['$event'])
    onBlur(event: any): void {
        this.blurred = true
    }

    @HostListener('window:mousemove', ['$event'])
    onMouseMove(event: any): void {
        if (this.blurred === true && this.timedOut === true) {
            this.timedOut = false
            this.queryConfirmedSessions(true)
            this.queryUnmatchedSessions(true)
        }
    }

    ngOnInit() {
        this.waitForRequiredDataToInitialize()
        this.registerForBlockStatusUpdates()
        this.registerForConfirmedSessionUpdates()
        this.registerForSelectedSessionUpdates()

        // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
        try {
            let now = Date.now()
            if (now >= FEATURE_TWENTY_FOUR_HOUR_BANNER_EXPIRY) {
                this.showFeatureBanner = false
            } else {
                this.showFeatureBanner =
                    localStorage.getItem(FEATURE_TWENTY_FOUR_HOUR_BANNER_DISMISSED) !== 'true'
            }
        } catch (e) {
            this.showFeatureBanner = true
        }
    }

    public onReceiveScrollbarWidth(width: number): void {
        this.scrollbarWidth = width
    }
    private waitForRequiredDataToInitialize(): void {
        this.userService.currentUser
            .pipe(
                filter((currentUser) => currentUser?.timeZone != null),
                take(1),
                switchMap((initialUser) => {
                    // Needed to correctly initialize the calendar header and receive
                    // the calendarStartTime
                    this.currentUser = initialUser

                    return this.userService.currentUser.pipe(
                        combineLatestWith(
                            this.calendarViewService.numberOfDaysToShow$,
                            this.calendarViewService.calendarStartTime$,
                            this.sessionSettingsService.sessionDurationSetting$,
                        ),
                        filter(
                            ([currentUser, numberOfDaysToShow, calendarStartDayMidnight]) =>
                                currentUser?.timeZone != null &&
                                numberOfDaysToShow != null &&
                                calendarStartDayMidnight != null,
                        ),
                        takeUntil(this.ngUnsubscribe),
                    )
                }),
            )
            .subscribe(
                ([currentUser, numberOfDaysToShow, calendarStartDayMidnight, sessionDuration]) => {
                    this.currentUser = currentUser
                    this.numberOfDaysToShow = numberOfDaysToShow
                    this.sessionDuration = sessionDuration
                    this.calendarStartDayMidnight = calendarStartDayMidnight
                    this.endTimeToRetrieve = this.calculateEndTimeToRetrieve()
                    this.updateDayArrays()
                    if (!this.initialized) {
                        this.initialized = true
                        this.startSessionDataQueries()
                        this.registerForDateRangeUpdates()
                    } else {
                        this.dateRange$.next({
                            startTime: this.calendarStartDayMidnight,
                            endTime: this.endTimeToRetrieve,
                        })
                    }
                },
            )
    }

    private registerForDateRangeUpdates(): void {
        this.dateRange$
            .pipe(
                distinctUntilChanged(
                    (prev, curr) =>
                        prev.startTime === curr.startTime && prev.endTime === curr.endTime,
                ),
                takeUntil(this.ngUnsubscribe),
                debounceTime(2500),
            )
            .subscribe((dateRange) => {
                const now = this.utilsService.getCurrentTimestamp()

                // Trigger a query if we haven't already loaded the time range
                // and the time range is in the future
                if (
                    now < this.endTimeToRetrieve &&
                    (this.calendarStartDayMidnight < dateRange.endTime ||
                        this.endTimeToRetrieve > dateRange.startTime)
                ) {
                    this.queryUnmatchedSessions(true)
                }
            })
    }

    private registerForBlockStatusUpdates(): void {
        this.userRelationshipService.userRelationUpdated
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((actionData: UserRelationshipActionTaken) => {
                if (actionData.action === 'block' || actionData.action === 'unblock') {
                    // Refresh our confirmed and available times data to reflect
                    // the new status
                    this.queryConfirmedSessions(true)
                    this.queryUnmatchedSessions(true)
                }
            })
    }

    private registerForSelectedSessionUpdates(): void {
        this.selectedSessionService.selectedSessions$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedSessions: SelectedSession[]) => {
                this.selectedSessions = [...selectedSessions]
            })
    }

    private registerForConfirmedSessionUpdates(): void {
        this.confirmedSessionsService.confirmedSessionsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((confirmedSessions: Container<ConfirmedSessionsResponse>) => {
                this.querySessionIfUnmatchedAtStartTime(confirmedSessions)
                //this.currentTime = this.util.getCurrentTimestamp()
            })

        this.confirmedSessionsService.lateSessionCanceled
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((wasCanceled) => {
                if (wasCanceled === true) {
                    this.dialogService.openLateSessionCanceledNotification()
                }
            })

        this.confirmedSessionsService.forceRematchNeeded
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((session) => {
                if (session) {
                    this.dialogService.openForceRematchModal(
                        session.externalId,
                        session.sessionTime,
                        session.duration,
                    )
                }
            })
    }

    private startSessionDataQueries() {
        // Start Polling Confirmed/Unmatched Sessions
        if (this.endTimeToRetrieve && !this.pollTimerStarted) {
            this.triggerNotifications(this.currentUser)
            this.pollTimerStarted = true
            let temp_sub: any = timer(0, 120000)
            let temp_sub2: any = timer(0, 122000)

            this.ngZone.runOutsideAngular(() => {
                temp_sub.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
                    this.ngZone.run(() => {
                        this.queryUnmatchedSessions()
                        if (this.blurred) {
                            this.timedOut = true
                        }
                    })
                })

                temp_sub2.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
                    // TODO: Update how tiles are udpated when a session
                    // ends that doesn't invlove passing in the current time
                    this.ngZone.run(() => {
                        //TODO: Revisit and add currentTime logic
                        //this.currentTime = this.util.getCurrentTimestamp()
                        this.queryConfirmedSessions()
                        if (this.blurred) {
                            this.timedOut = true
                        }
                    })
                })
            })
        }
    }

    private queryUnmatchedSessions(override: boolean = false) {
        if (this.blurred === false || override) {
            this.availableTimesService
                .queryAvailableTimes(this.calendarStartDayMidnight, this.endTimeToRetrieve)
                .subscribe(() => {
                    // TODO: Add this logic
                    //this.retrievedUpToTimestamp = this.endTimeToRetrieve
                    //this.retrievedFromTimestamp = this.startTimeToRetrieve
                })
        }
    }

    private queryConfirmedSessions(override: boolean = false) {
        if (this.blurred === false || override) {
            this.confirmedSessionsService.getConfirmedSessions().subscribe(
                (sessions) => {},
                (error) => {},
            )
        }
    }

    private querySessionIfUnmatchedAtStartTime(sessions: Container<ConfirmedSessionsResponse>) {
        // if there is an unmatched session starting in the next 2 minutes,
        // schedule a query to update its info when the session starts
        const length = sessions.length()
        let nowEpoch = this.utilsService.getCurrentTimestamp()
        this.clearUnmatchedSessionStartingTimeout()

        for (let i = 0; i < length; ++i) {
            const session = sessions.getByIndex(i)
            const timeDifferenceInMilliSeconds = session.sessionTime - nowEpoch

            if (
                timeDifferenceInMilliSeconds >= 0 &&
                timeDifferenceInMilliSeconds < TWO_MINS_IN_MSECS &&
                !session.partnerId
            ) {
                this.unmatchedSessionStartingTimeout = setTimeout(() => {
                    this.confirmedSessionsService.updateConfirmedSession(session.externalId)
                    this.clearUnmatchedSessionStartingTimeout()
                }, timeDifferenceInMilliSeconds)
            }
        }
    }

    private clearUnmatchedSessionStartingTimeout() {
        if (this.unmatchedSessionStartingTimeout) {
            clearTimeout(this.unmatchedSessionStartingTimeout)
            this.unmatchedSessionStartingTimeout = null
        }
    }

    private updateDayArrays(): void {
        if (this.calendarStartDayMidnight && this.numberOfDaysToShow) {
            this.days = Array.from({ length: this.numberOfDaysToShow }, (_, index) => {
                return {
                    startOfDayInMs: moment(this.calendarStartDayMidnight)
                        .add(index, 'day')
                        .valueOf(),
                    isToday: this.timeUtilitiesService.isToday(
                        moment(this.calendarStartDayMidnight).add(index, 'day').valueOf(),
                        this.currentUser.timeZone,
                    ),
                }
            })
        }
    }

    private calculateEndTimeToRetrieve(): number {
        if (this.numberOfDaysToShow && this.calendarStartDayMidnight) {
            return this.numberOfDaysToShow * MS_IN_ONE_DAY + this.calendarStartDayMidnight
        }
        return 0
    }

    triggerNotifications(user: User) {
        // Order of notification modals is:
        // 1) Welcome Checklist modal if applicable
        // 2) Any pending invite modals
        // 3) Feature release modal if any
        // 4) Year in Review modal if any
        setTimeout(() => {
            if (this.lUtil.getComingFromSignup()) {
                this.lUtil.setComingFromSignUp(false)
                this.lUtil.markOnboardingComplete(user)
            }

            if (this.isWelcomeChecklistActive()) {
                this.welcomeChecklistService.openWelcomeChecklistAtPageLoad()
            }

            this.displayGroupInviteNotifications(user)
            this.displayFeatureModal(user)
            //this.displayYearInReviewModal(user)
        }, 500)
    }

    // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
    displayFeatureModal(user: User) {
        /*
        if (!this.featureCheckService.hasViewedGoogleCalendarReleaseModal(user)) {
            this.dialogService.openFeatureLaunchModal(FeatureReleaseModalComponent)
        }
        */

        // Only show the domain updated notification if the user has not yet seen it
        // and if we are not showing the "domain will update" notification (Ie. This is the
        // app.focusmate.com version of the application)
        if (
            !this.featureCheckService.hasViewedDomainUpdatedNotification(user) &&
            environment.showDomainUpdatedConfirmation
        ) {
            this.dialogService.openDomainUpdatedNotificationModal()
        }
    }

    // KEEPING THIS CODE AROUND FOR NEXT YEAR IN FOCUS
    displayYearInReviewModal(user: User) {
        if (user.yearlyReview) {
            if (!this.featureCheckService.hasViewedYearInReviewModal(user)) {
                this.dialogService.openYearInReviewModal()
            }
        }
    }

    // KEEPING THIS CODE AROUND FOR NEXT YEAR IN FOCUS
    onClickYearInFocusButton() {
        this.analyticsService.logClickedEvent('My 2023 in Focus', 'Calendar View')
        this.dialogService.openYearInReviewModal()
    }

    displayGroupInviteNotifications(user: User) {
        let groupInfo = user.group

        for (let group of groupInfo) {
            if (group.groupMember === false && group.groupInviteNotified === false) {
                this.dialogService.openGroupInviteNotification(group, this.currentUser.photo)
            }
        }
    }

    /* Banner and Feature modal logic */
    onClickBannerDismissed() {
        // KEEPING THIS CODE AROUND FOR THE NEXT FEATURE BANNER
        this.showFeatureBanner = false
        try {
            localStorage.setItem(FEATURE_TWENTY_FOUR_HOUR_BANNER_DISMISSED, 'true')
        } catch (e) {}
    }

    onClickClearAll() {
        this.selectedSessionService.deleteAllSelectedSessions()
    }

    onClickBookSessions() {
        const selectedSessionLength = this.selectedSessions.length
        let bookSessionInfo: BookSessionInfo[] = []

        for (let i = 0; i < selectedSessionLength; i++) {
            const selectedSession: SelectedSession = this.selectedSessions[i]
            const preferences: BookSessionPreferences = {
                favorites: {
                    value: selectedSession.sessionPreferenceFavorites,
                },
                quietMode: {
                    value: selectedSession.quietMode,
                },
            }

            bookSessionInfo.push({
                sessionTime: selectedSession.sessionTime,
                duration: selectedSession.sessionDuration,
                title: '',
                userAvailabilityCode: '',
                userId: '',
                preferences,
                activityType: selectedSession.activityType,
            })
        }

        this.bookingInProgress = true
        this.bookSessionsService
            .bookSessions(bookSessionInfo, this.currentUser.timeZone, BookingSource.CalendarFooter)
            .pipe(
                take(1),
                finalize(() => (this.bookingInProgress = false)),
            )
            .subscribe(
                (res) => {
                    let parsedRes = this.bookSessionsService.parseBookResponse(res)

                    if (parsedRes['numSuccess'] !== 0) {
                        this.dialogService.openConfirmTransaction(
                            parsedRes['numSuccess'] === 1
                                ? parsedRes['numSuccess'] + ' session booked!'
                                : parsedRes['numSuccess'] + ' sessions booked!',
                        )
                    }

                    if (this.currentUser.emailVerified === false) {
                        this.dialogService
                            .openEmailVerificationDialog(MODAL_VERIFY_EMAIL_BOOKING)
                            .afterClosed()
                            .pipe(take(1))
                            .subscribe(() => {
                                this.processParsedResult(parsedRes)
                            })
                    } else {
                        this.processParsedResult(parsedRes)
                    }
                },
                (error) => {
                    this.errorHandlerService.handleErrorResponse(error)
                    this.selectedSessionService.deleteAllSelectedSessions()
                },
            )
    }

    processParsedResult(parsedRes) {
        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (
            !this.isWelcomeChecklistActive() &&
            !this.currentUser.properties.viewedSegmentationSurveyModal &&
            this.currentUser.compSessions >= 3
        ) {
            this.dialogService.openSegmentationSurveyModal()
        } else if (
            !this.isWelcomeChecklistActive() &&
            this.rewardsReferralService.shouldSeeReferralPrompt(this.currentUser)
        ) {
            this.dialogService.openReferralPromptModal()
        }
    }

    ngOnDestroy() {
        this.clearUnmatchedSessionStartingTimeout()
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
